import React, { useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

interface AddDomainDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (hostname: string) => void;
  isSubmitting: boolean;
}

const normalizeHostname = (value: string): string =>
  value.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/+$/, "");

/** Must be a subdomain (at least 3 parts: sub.domain.tld) */
const isSubdomain = (value: string): boolean => {
  const cleaned = normalizeHostname(value);
  if (!cleaned || cleaned.includes(" ")) return false;
  const parts = cleaned.split(".");
  if (parts.length < 3) return false;
  return /^([a-z0-9]([a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}$/.test(cleaned);
};

/** Valid hostname but root domain (only 2 parts: domain.tld) */
const isRootDomain = (value: string): boolean => {
  const cleaned = normalizeHostname(value);
  if (!cleaned || cleaned.includes(" ")) return false;
  const parts = cleaned.split(".");
  return parts.length === 2 && /^[a-z0-9]([a-z0-9-]*[a-z0-9])?\.[a-z]{2,}$/.test(cleaned);
};

const AddDomainDialog: React.FC<AddDomainDialogProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting,
}) => {
  const [hostname, setHostname] = useState("");

  const normalized = normalizeHostname(hostname);
  const valid = isSubdomain(hostname);
  const isRoot = isRootDomain(hostname);

  const handleSubmit = () => {
    if (!valid) return;
    onSubmit(normalized);
  };

  const handleClose = () => {
    setHostname("");
    onClose();
  };

  const getHelperText = () => {
    if (!normalized) return "Enter a subdomain (e.g. app.example.com)";
    if (isRoot)
      return "Root domains are not supported. Use a subdomain like app.example.com";
    if (!valid) return "Enter a valid subdomain (e.g. app.example.com)";
    return "Enter a subdomain (e.g. app.example.com)";
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Custom Domain</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity="info" variant="outlined">
            <Typography variant="body2">
              Only subdomains are supported (e.g.{" "}
              <strong>app.example.com</strong>). Root/apex domains (e.g.
              example.com) cannot use CNAME records and are not supported at this
              time.
            </Typography>
          </Alert>
          <TextField
            label="Subdomain"
            placeholder="e.g. app.example.com, portal.example.com"
            value={hostname}
            onChange={(e) => setHostname(e.target.value)}
            fullWidth
            required
            helperText={getHelperText()}
            error={normalized.length > 0 && !valid}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!valid || isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Add Domain"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDomainDialog;
